/**
 * Video Preview Manager - Handles hover preview and click-to-play for YouTube and HTML5 videos
 */
class VideoPreviewManager {
    constructor(options = {}) {
        this.options = {
            iframeSelector: '.video-item iframe, .unboxing-item iframe',
            videoSelector: '.video-item video, .unboxing-item video',
            previewDelay: 550,
            overlayClass: 'wg-video-overlay',
            idPrefix: 'wg-video-',
            ...options
        };
        
        this.players = new Map();
        this.states = new Map();
        this.timers = new Map();
        this.overlays = new Map();
        this.ytAPI = null;
    }
    
    /**
     * Initialize the video preview system
     */
    async init() {
        const elements = this.collectElements();
        if (!elements.iframes.length && !elements.videos.length) return;
        
        // Prepare elements
        elements.iframes.forEach((iframe, idx) => this.prepareIframe(iframe, idx));
        elements.videos.forEach((video, idx) => this.prepareVideo(video, idx));
        
        // Load YouTube API if we have iframes
        if (elements.iframes.length) {
            this.ytAPI = await this.loadYouTubeAPI();
            elements.iframes.forEach(iframe => this.setupYouTubePlayer(iframe));
        }
        
        // Setup HTML5 videos
        elements.videos.forEach(video => this.setupHTML5Player(video));
    }
    
    /**
     * Collect all video elements from the DOM
     */
    collectElements() {
        return {
            iframes: Array.from(document.querySelectorAll(this.options.iframeSelector)),
            videos: Array.from(document.querySelectorAll(this.options.videoSelector))
        };
    }
    
    /**
     * Prepare iframe element with necessary attributes
     */
    prepareIframe(iframe, idx) {
        // Store original src
        if (!iframe.dataset.wgOriginalSrc) {
            iframe.dataset.wgOriginalSrc = iframe.src;
        }
        
        // Ensure enablejsapi=1 for YouTube
        try {
            const url = new URL(iframe.src);
            if (!url.searchParams.get('enablejsapi')) {
                url.searchParams.set('enablejsapi', '1');
                iframe.src = url.toString();
            }
        } catch (e) {
            console.warn('Invalid iframe URL:', e);
        }
        
        // Ensure unique ID
        if (!iframe.id) {
            iframe.id = `${this.options.idPrefix}${Date.now()}-${idx}`;
        }
        
        // Initialize state
        this.initializeState(iframe.id);
    }
    
    /**
     * Prepare video element with necessary attributes
     */
    prepareVideo(video, idx) {
        // Ensure unique ID
        if (!video.id) {
            video.id = `${this.options.idPrefix}html5-${Date.now()}-${idx}`;
        }
        
        // Store original src
        if (!video.dataset.wgOriginalSrc) {
            video.dataset.wgOriginalSrc = video.currentSrc || video.src || '';
        }
        
        // Set required attributes for autoplay
        video.setAttribute('playsinline', '');
        video.muted = true;
        video.preload = 'auto';
        
        // Initialize state
        this.initializeState(video.id);
    }
    
    /**
     * Initialize state for a player
     */
    initializeState(id) {
        this.states.set(id, {
            clicked: false,
            hovering: false,
            originalSrc: null
        });
    }
    
    /**
     * Load YouTube IFrame API
     */
    loadYouTubeAPI() {
        return new Promise((resolve) => {
            if (window.YT && window.YT.Player) {
                return resolve(window.YT);
            }
            
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api';
            const firstScript = document.getElementsByTagName('script')[0];
            firstScript.parentNode.insertBefore(tag, firstScript);
            
            window.onYouTubeIframeAPIReady = () => resolve(window.YT);
        });
    }
    
    /**
     * Setup YouTube player
     */
    setupYouTubePlayer(iframe) {
        const id = iframe.id;
        const player = new this.ytAPI.Player(id, {
            events: {
                onReady: () => this.onPlayerReady(id, 'youtube')
            }
        });
        
        this.players.set(id, {
            type: 'youtube',
            element: player,
            iframe: iframe
        });
        
        this.createOverlay(iframe, id);
    }
    
    /**
     * Setup HTML5 video player
     */
    setupHTML5Player(video) {
        const id = video.id;
        
        this.players.set(id, {
            type: 'html5',
            element: video
        });
        
        this.onPlayerReady(id, 'html5');
        this.createOverlay(video, id);
    }
    
    /**
     * Handle player ready event
     */
    onPlayerReady(id, type) {
        // Player is ready, overlay will handle interactions
    }
    
    /**
     * Create overlay for video element
     */
    createOverlay(element, id) {
        const container = element.parentElement || element;
        
        // Ensure container can position overlay
        const computedStyle = window.getComputedStyle(container);
        if (computedStyle.position === 'static') {
            container.style.position = 'relative';
        }
        
        // Create overlay element
        const overlay = document.createElement('div');
        overlay.className = this.options.overlayClass;
        Object.assign(overlay.style, {
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            background: 'transparent',
            zIndex: '9999',
            cursor: 'pointer'
        });
        overlay.dataset.wgFor = id;
        
        // Bind event handlers
        this.bindOverlayEvents(overlay, id);
        
        // Add to DOM and track
        container.appendChild(overlay);
        this.overlays.set(id, overlay);
    }
    
    /**
     * Bind events to overlay
     */
    bindOverlayEvents(overlay, id) {
        let enterHandled = false;
        
        const handleEnter = () => {
            if (!enterHandled) {
                enterHandled = true;
                this.handleHoverStart(id);
            }
        };
        
        const handleLeave = () => {
            enterHandled = false; // Reset the flag so it can trigger again
            this.handleHoverEnd(id);
        };
        
        const handleClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.handleClick(id);
        };
        
        overlay.addEventListener('mouseenter', handleEnter);
        overlay.addEventListener('mouseleave', handleLeave);
        overlay.addEventListener('click', handleClick);
        overlay.addEventListener('touchstart', handleClick, { passive: false });
    }
    
    /**
     * Handle hover start
     */
    handleHoverStart(id) {
        const state = this.states.get(id);
        if (!state || state.clicked) return;
        
        state.hovering = true;
        
        // Clear any existing timer
        this.clearTimer(id);
        
        // Start preview after delay
        const timer = setTimeout(() => {
            this.playPreview(id);
            this.timers.delete(id);
        }, this.options.previewDelay);
        
        this.timers.set(id, timer);
    }
    
    /**
     * Handle hover end
     */
    handleHoverEnd(id) {
        const state = this.states.get(id);
        if (!state) return;
        
        state.hovering = false;
        
        // Clear any pending timer
        this.clearTimer(id);
        
        // Reset player (always reset on hover end, regardless of click state)
        this.resetPlayer(id);
    }
    
    /**
     * Handle click on overlay
     */
    handleClick(id) {
        const state = this.states.get(id);
        if (!state) return;
        
        state.clicked = true;
        
        // Clear any pending timer
        this.clearTimer(id);
        
        // Remove overlay
        this.removeOverlay(id);
        
        // Play with sound
        this.playWithSound(id);
    }
    
    /**
     * Clear timer for player
     */
    clearTimer(id) {
        const timer = this.timers.get(id);
        if (timer) {
            clearTimeout(timer);
            this.timers.delete(id);
        }
    }
    
    /**
     * Play preview (muted)
     */
    playPreview(id) {
        const player = this.players.get(id);
        if (!player) return;
        
        try {
            if (player.type === 'html5') {
                player.element.muted = true;
                const promise = player.element.play();
                if (promise && typeof promise.catch === 'function') {
                    promise.catch(() => {}); // Ignore autoplay errors
                }
            } else if (player.type === 'youtube') {
                if (player.element.mute) player.element.mute();
                if (player.element.playVideo) player.element.playVideo();
            }
        } catch (e) {
            console.warn('Error playing preview:', e);
        }
    }
    
    /**
     * Reset player to initial state
     */
    resetPlayer(id) {
        const state = this.states.get(id);
        if (!state) return;
        
        // Only reset if not currently hovering or if clicked (to stop playback after click)
        if (state.hovering && !state.clicked) return;
        
        const player = this.players.get(id);
        if (!player) return;
        
        try {
            if (player.type === 'html5') {
                player.element.pause();
                player.element.currentTime = 0;
            } else if (player.type === 'youtube') {
                if (player.element.pauseVideo) player.element.pauseVideo();
                if (player.element.seekTo) player.element.seekTo(0);
                
                // Restore original src for iframe
                const iframe = player.iframe;
                if (player.element.stopVideo) player.element.stopVideo();
                
            }
        } catch (e) {
            console.warn('Error resetting player:', e);
        }
    }
    
    /**
     * Play with sound (after click)
     */
    playWithSound(id) {
        const player = this.players.get(id);
        if (!player) return;
        
        const attemptPlay = () => {
            try {
                if (player.type === 'html5') {
                    player.element.muted = false;
                    const promise = player.element.play();
                    if (promise && typeof promise.catch === 'function') {
                        promise.catch(() => {}); // Ignore autoplay errors
                    }
                } else if (player.type === 'youtube') {
                    if (player.element.unMute) player.element.unMute();
                    if (player.element.getPlayerState && player.element.playVideo) {
                        const state = player.element.getPlayerState();
                        const YT = this.ytAPI;
                        if (state !== YT.PlayerState.PLAYING) {
                            player.element.playVideo();
                        }
                    }
                }
            } catch (e) {
                console.warn('Error playing with sound:', e);
            }
        };
        
        // For YouTube, player methods might not be ready immediately
        if (player.type === 'youtube' && (!player.element.playVideo || !player.element.unMute)) {
            let attempts = 0;
            const maxAttempts = 20;
            const pollInterval = setInterval(() => {
                attempts++;
                if (player.element.playVideo && player.element.unMute) {
                    clearInterval(pollInterval);
                    attemptPlay();
                } else if (attempts >= maxAttempts) {
                    clearInterval(pollInterval);
                }
            }, 100);
        } else {
            attemptPlay();
        }
    }
    
    /**
     * Remove overlay
     */
    removeOverlay(id) {
        const overlay = this.overlays.get(id);
        if (overlay && overlay.parentNode) {
            overlay.parentNode.removeChild(overlay);
            this.overlays.delete(id);
        }
    }
    
    /**
     * Destroy the manager and clean up
     */
    destroy() {
        // Clear all timers
        this.timers.forEach((timer, id) => this.clearTimer(id));
        
        // Remove all overlays
        this.overlays.forEach((overlay, id) => this.removeOverlay(id));
        
        // Reset all players
        this.players.forEach((player, id) => this.resetPlayer(id));
        
        // Clear collections
        this.players.clear();
        this.states.clear();
        this.timers.clear();
        this.overlays.clear();
    }
}

// Initialize on DOM ready
(function() {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            const manager = new VideoPreviewManager();
            manager.init();
        });
    } else {
        const manager = new VideoPreviewManager();
        manager.init();
    }
})();